import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { keyframes } from "@emotion/react";
import html2canvas from "html2canvas";
import { useDispatch, useSelector } from "react-redux";
import SendButton from "../Form/Button/SendButton";
import { ChatInput } from "./aiCommon";
import { getIsGenerating, useAiSidebarStateObject } from "./aiSidebarHooks";
import MarkdownContainer from "./MarkdownContainer";
import Markdown from "./Markdown";
import { MessageBubble } from "./MessageBubble";
import SidebarContainer from "./SidebarContainer";
import ThinkingBlock from "./ThinkingBlock";
import ToolUseBlock from "./ToolUseBlock";
import ToolResponseBlock from "./ToolResponseBlock";
import QueryBlock from "./QueryBlock";
import StatusBlock from "./StatusBlock";
import PdfExportSection from "./PdfExport";
import { useShouldShowTtgFeature } from "../TtgFeature";
import AlertMessage from "../Alerts/AlertMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/pro-light-svg-icons";
import {
  resetAiError,
  submitAiMessageFeedback,
} from "../../store/actions/dashboard/dashboard";
import OrderedMessages from "./SidebarMessages/OrderedMessages";
import { pulse } from "./SidebarMessages/SidebarLoader";
import MessageFeedback from "./MessageFeedback";
import ResultChip from "./ResultChip";
import {
  selectActiveResult,
  selectResultsHistory,
} from "../../store/selectors/aiResults";
import useMarkerIO from "../../hooks/useMarkerIO";

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// const loadingGradientAnimation = keyframes`
//   0% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// `;

const OuterContainer = styled.div`
  text-align: left;
`;

const EmbeddedContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: left;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  margin-top: 20px;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 15px;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.type === "light"
        ? props.theme.gray.gray200
        : props.theme.gray.gray800};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.type === "light"
        ? props.theme.gray.gray400
        : props.theme.gray.gray600};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      props.theme.type === "light"
        ? props.theme.gray.gray500
        : props.theme.gray.gray500};
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 250px;
  position: relative;
  overflow: hidden;

  .loading-text {
    margin-top: 10px;
    font-size: 15px;
    letter-spacing: 1px;
    font-weight: 500;
    color: #5b6bfd;
  }
`;

const Loader = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  display: flex;
  justify-content: center;
  align-items: center;

  .dots-container {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
  }

  .dot {
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #5b6bfd, #8a94ff);
    border-radius: 50%;
    box-shadow: 0 0 15px rgba(91, 107, 253, 0.5);
    animation: ${pulse} 2s infinite;
  }

  .dot:nth-of-type(1) {
    animation-delay: 0s;
  }

  .dot:nth-of-type(2) {
    animation-delay: 0.3s;
  }

  .dot:nth-of-type(3) {
    animation-delay: 0.6s;
  }
`;

const HeaderContainer = styled.div`
  position: relative;
  padding-bottom: 15px;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .header-actions {
    display: flex;
    align-items: center;
  }

  h2 {
    margin: 0;
    color: #5b6bfd;
    font-size: 22px;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  h2::before {
    content: "";
    display: inline-block;
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%235B6BFD'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92c-.5.51-.86.97-1.04 1.69-.08.32-.13.68-.13 1.14h-2v-.5c0-.46.08-.9.22-1.31.2-.58.53-1.1.95-1.52l1.24-1.26c.46-.44.68-1.1.55-1.8-.13-.72-.69-1.33-1.39-1.39-1.11-.31-2.14.32-2.47 1.27-.12.35-.43.58-.79.58-.5 0-.86-.51-.71-.99.47-1.58 1.89-2.82 3.59-2.82 2.22 0 3.99 1.88 3.79 4.11-.14 1.24-.78 2.25-1.91 3.03z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
    margin-right: 8px;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #5b6bfd;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  margin-left: 16px;

  &:hover {
    background: #4857e9;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContentTitle = styled.h1`
  color: #5b6bfd;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 30px;
  text-align: center;
`;

// Chat components
const ChatContainer = styled.div`
  margin-top: 20px;
  border-top: 1px solid ${(props) => props.theme.borderColor};
  padding-top: 16px;
  display: flex;
  flex-direction: column;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
`;

const AiMessageWrapper = styled.div`
  width: 100%;
`;

const ChatInputContainer = styled.div`
  padding: 16px 0 48px 0;
  border-top: 1px solid ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.background.primary};
`;

const InputContainer = styled.div`
  display: flex;
  gap: 10px;
  position: relative;
`;

const StopButton = styled.button`
  background: transparent;
  color: #e74c3c;
  border: 2px solid #e74c3c;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(231, 76, 60, 0.1);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StepContainer = styled.div`
  margin-bottom: 2px;
`;

const ErrorContainer = styled.div`
  font-size: 12px;
  display: flex;
  align-items: center;
  width: 100%;

  & > div > div {
    padding: 0px;
  }

  & > svg {
    cursor: pointer;
    font-size: 16px;
    margin-left: 20px;
    &:hover {
      color: ${(props) => props.theme.text.primary};
    }
  }
`;

const StepBlock = ({ step, index, currentStepIndex }) => {
  if (!step) return null;

  let component;

  switch (step.type) {
    case "thinking":
      component = (
        <ThinkingBlock
          step={step}
          content={step.content}
          stepIndex={index}
          totalSteps={0} // We'll calculate this in the parent
          currentStepIndex={currentStepIndex}
        />
      );
      break;
    case "tool_use":
      component = (
        <ToolUseBlock
          toolName={step.content}
          stepIndex={index}
          currentStepIndex={currentStepIndex}
        />
      );
      break;
    case "tool":
      component = <ToolResponseBlock content={step.content} />;
      break;
    case "debug":
      component = <QueryBlock content={step.content} />;
      break;
    case "status":
      component = (
        <StatusBlock
          statusText={step.content}
          stepIndex={index}
          currentStepIndex={currentStepIndex}
        />
      );
      break;
    default:
      return null;
  }

  return <StepContainer>{component}</StepContainer>;
};

/**
 * @param isOpen
 * @param data
 * @param initialQuestion {?string}
 * @param onClose
 * @param loading
 * @param title
 * @param liftedStateObject {object=} If you want states to be managed from
 *  outside, then you can pass what's returned from useAiSidebarStateObject()
 *  from outside into this component.
 * @param onSendFollowUpQuestion {(question: string) => void}
 * @param chartRef {Element}
 */
export default function AiSidebar({
  isOpen,
  data = "",
  initialQuestion,
  onClose,
  loading = false,
  title = "",
  liftedStateObject,
  onSendFollowUpQuestion,
  onStop,
  chartRef,
  powerEditorMode,
  predefinedQuestion,
  embedded = false,
  onSelectResult,
}) {
  const insideStateObject = useAiSidebarStateObject();
  const stateObject = liftedStateObject ?? insideStateObject;
  const [chartImage, setChartImage] = stateObject.chartImageState;
  const [question, setQuestion] = stateObject.questionState;
  const [lastQuestion, setLastQuestion] = useState(predefinedQuestion);
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState(null);
  const aiSidebar = useSelector((state) => state.dashboard.aiSidebar);
  const {
    messages,
    error,
    conversationId,
    streamingMessageIndex,
    activeResultMessageId,
  } = aiSidebar;
  const resultsHistory = useSelector(selectResultsHistory);
  const activeResult = useSelector(selectActiveResult);
  const resultsHistoryByMessageId = useMemo(() => {
    const map = new Map();
    resultsHistory.forEach((r) => map.set(r.messageId, r));
    return map;
  }, [resultsHistory]);
  const messageRefs = useRef({});
  const resolvedTitle = title || aiSidebar.title || "";
  const isGenerating = getIsGenerating(aiSidebar, powerEditorMode);
  const isTtgUser = useShouldShowTtgFeature();
  const dispatch = useDispatch();
  const conteinerRef = useRef(null);
  const { captureWithContext } = useMarkerIO();
  const tenantId = useSelector((state) => state.layout.tenantId);
  const siteName = useSelector((state) => state.layout.siteName);
  const authName = useSelector((state) => state.auth.name);
  const authEmail = useSelector((state) => state.auth.email);

  const handleThumbsDown = (messageId) => {
    const transcript = messages.slice(-20).map((m) => ({
      type: m.type,
      content: typeof m.content === "string" ? m.content.substring(0, 500) : "",
    }));

    captureWithContext({
      conversationId,
      messageId,
      tenantId,
      tenantName: siteName,
      userName: authName,
      userEmail: authEmail,
      visualizationId: aiSidebar.visualizationId || null,
      chatMode: embedded ? "embedded" : "sidebar",
      chatTranscript: JSON.stringify(transcript),
      pageUrl: window.location.href,
    });
  };

  // Calculate current step index for thinking and tool blocks
  const getCurrentStepIndex = (messageIndex, stepIndex) => {
    if (!isGenerating) return -1; // No step is currently generating

    // Use streamingMessageIndex to identify the message being streamed into
    const generatingMessageIndex =
      streamingMessageIndex != null ? streamingMessageIndex : -1;

    if (generatingMessageIndex < 0) return -1;

    const lastGeneratingMessage = messages[generatingMessageIndex];
    if (!lastGeneratingMessage || lastGeneratingMessage.type !== "ai") {
      return -1;
    }

    // If this is not the generating message, all steps are complete
    if (generatingMessageIndex !== messageIndex) {
      return -1;
    }

    // For the generating message, find the last active step (thinking or tool_use)
    // Thinking is "active" until content starts, or tool_use/tool arrives
    // tool_use is "active" until its tool result arrives
    const steps = lastGeneratingMessage.steps || [];

    let lastActiveStepIndex = -1;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].type === "thinking") {
        const hasMovedPast = steps
          .slice(i + 1)
          .some(
            (step) =>
              step.type === "tool" ||
              step.type === "tool_use" ||
              step.type === "content_start"
          );
        if (!hasMovedPast) {
          lastActiveStepIndex = i;
          break;
        }
      } else if (steps[i].type === "tool_use") {
        const hasToolResponse = steps
          .slice(i + 1)
          .some((step) => step.type === "tool");
        if (!hasToolResponse) {
          lastActiveStepIndex = i;
          break;
        }
      } else if (steps[i].type === "status") {
        // Status is active until any subsequent step arrives
        const hasNextStep = steps.length > i + 1;
        if (!hasNextStep) {
          lastActiveStepIndex = i;
          break;
        }
      }
    }

    return lastActiveStepIndex === stepIndex ? stepIndex : -1;
  };
  const messagesEndRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const chatInputRef = useRef(null);
  const pdfExportRef = useRef(null);

  useEffect(() => {
    if (embedded) return;
    const captureChart = async () => {
      if (chartRef && !loading) {
        try {
          const canvas = await html2canvas(chartRef, {
            scale: 2, // Higher quality
            backgroundColor: null,
            useCORS: true,
          });
          setChartImage(canvas.toDataURL("image/png"));
        } catch (error) {
          console.error("Error capturing chart:", error);
        }
      }
    };

    captureChart();
  }, [chartRef, loading, setChartImage, embedded]);

  useEffect(() => {
    // Focus when component mounts
    setTimeout(() => chatInputRef.current?.focus(), 1000);
  }, []);

  useEffect(() => {
    // Scroll to the bottom whenever messages change, unless the user has
    // pinned a past result — in which case they're inspecting history.
    if (activeResultMessageId) return;
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeResultMessageId]);

  useEffect(() => {
    // When the user picks a past result, bring its message into view in the chat.
    if (!activeResultMessageId) return;
    const node = messageRefs.current[activeResultMessageId];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeResultMessageId]);

  const handleSendQuestion = (e) => {
    e.preventDefault();
    if (question.trim() === "") return;

    onSendFollowUpQuestion(question, isTtgUser);
    setLastQuestion(question);
    setQuestion("");
    if (chatInputRef.current) {
      chatInputRef.current.style.height = "40px";
    }
  };

  const handleExport = () => {
    if (pdfExportRef.current) {
      pdfExportRef.current.handleExport();
    }
  };

  const CONFIG_BLOCK_REGEX = /CONFIG_START[\s\S]*?CONFIG_END/g;
  const CONFIG_START_TO_END_REGEX = /CONFIG_START[\s\S]*$/g;

  const formatJsonConfigIfExist = (text) => {
    if (isTtgUser) {
      return text
        .replaceAll("CONFIG_START", "🌲\n```")
        .replaceAll("CONFIG_END", "```\n");
    }

    // If CONFIG_END exists → removes only the block
    // If it doesn’t → removes from CONFIG_START to end
    const hasConfigEnd = text.includes("CONFIG_END");
    const regex = hasConfigEnd ? CONFIG_BLOCK_REGEX : CONFIG_START_TO_END_REGEX;

    return text.replace(regex, "");
  };

  const chatContent = (
    <ContentContainer>
      <ScrollableContent ref={scrollableContentRef}>
        {!embedded && !powerEditorMode && (
          <ContentTitle>AI Insights</ContentTitle>
        )}
        {embedded && resolvedTitle && (
          <ContentTitle style={{ fontSize: 18, marginBottom: 16 }}>
            {resolvedTitle}
          </ContentTitle>
        )}
        {initialQuestion ? (
          <MessageGroup isUser>
            <MessageBubble isUser>{initialQuestion}</MessageBubble>
          </MessageGroup>
        ) : null}
        <MarkdownContainer>
          <Markdown>{data}</Markdown>
        </MarkdownContainer>

        {messages.length > 0 && (
          <ChatContainer>
            <MessagesContainer>
              {messages.map((message, index) => {
                if (message.type === "user") {
                  return (
                    <MessageGroup key={index} isUser={true}>
                      <MessageBubble isUser={true}>
                        {message.content}
                      </MessageBubble>
                    </MessageGroup>
                  );
                } else {
                  return (
                    <AiMessageWrapper
                      key={"message-" + index}
                      ref={(el) => {
                        if (message.id) messageRefs.current[message.id] = el;
                      }}
                      onMouseEnter={() => setHoveredMessageIndex(index)}
                      onMouseLeave={() => setHoveredMessageIndex(null)}
                    >
                      <OrderedMessages
                        message={message}
                        index={index}
                        error={error}
                        isGenerating={isGenerating}
                        getCurrentStepIndex={getCurrentStepIndex}
                        formatJsonConfigIfExist={formatJsonConfigIfExist}
                        Markdown={Markdown}
                        StepBlock={StepBlock}
                        MessageGroup={MessageGroup}
                        MessageBubble={MessageBubble}
                        MarkdownContainer={MarkdownContainer}
                        isTtgUser={isTtgUser}
                      />
                      {message.id && conversationId && (
                        <MessageGroup isUser={false}>
                          <MessageFeedback
                            messageId={message.id}
                            conversationId={conversationId}
                            currentFeedback={message.feedback}
                            isHovered={hoveredMessageIndex === index}
                            onThumbsDown={handleThumbsDown}
                            onFeedbackSubmit={(
                              messageId,
                              conversationId,
                              feedback
                            ) =>
                              dispatch(
                                submitAiMessageFeedback(
                                  conversationId,
                                  messageId,
                                  feedback
                                )
                              )
                            }
                          />
                        </MessageGroup>
                      )}
                      {onSelectResult &&
                      message.id &&
                      resultsHistoryByMessageId.has(message.id) ? (
                        <MessageGroup isUser={false}>
                          <ResultChip
                            active={activeResult?.messageId === message.id}
                            onClick={() => onSelectResult(message.id)}
                          />
                        </MessageGroup>
                      ) : null}
                    </AiMessageWrapper>
                  );
                }
              })}

              {error ? (
                <ErrorContainer>
                  <AlertMessage outline status="error" message={error} />
                  <FontAwesomeIcon
                    icon={faSync}
                    title="Retry"
                    onClick={() => {
                      // resend question on error and clear error
                      dispatch(resetAiError());
                      onSendFollowUpQuestion(lastQuestion, isTtgUser);
                    }}
                  />
                </ErrorContainer>
              ) : null}

              <div ref={messagesEndRef} />
            </MessagesContainer>
          </ChatContainer>
        )}
      </ScrollableContent>

      <ChatInputContainer>
        <form onSubmit={handleSendQuestion}>
          <InputContainer>
            <ChatInput
              placeholder={
                resolvedTitle
                  ? `Ask about ${resolvedTitle}...`
                  : "Ask about your data"
              }
              value={question}
              onValueChange={setQuestion}
              onSubmit={handleSendQuestion}
              disabled={loading || isGenerating}
              elementRef={chatInputRef}
            />
            {isGenerating && !error && onStop ? (
              <StopButton
                type="button"
                onClick={onStop}
                title="Stop generating"
                aria-label="Stop generating"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </StopButton>
            ) : (
              <SendButton
                type="submit"
                disabled={loading || isGenerating || !question.trim()}
              />
            )}
          </InputContainer>
        </form>
      </ChatInputContainer>
    </ContentContainer>
  );

  if (embedded) {
    return (
      <EmbeddedContainer ref={conteinerRef}>{chatContent}</EmbeddedContainer>
    );
  }

  return (
    <OuterContainer ref={conteinerRef}>
      <PdfExportSection
        ref={pdfExportRef}
        title={resolvedTitle}
        data={data}
        chartImage={chartImage}
      />

      <SidebarContainer isOpen={isOpen} widthState={stateObject.widthState}>
        <HeaderContainer>
          {!loading && (
            <>
              <h2>{resolvedTitle}</h2>
              <div className="header-actions">
                {data && (
                  <ExportButton onClick={handleExport}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                    </svg>
                    Export PDF
                  </ExportButton>
                )}
                <CloseButton onClick={onClose} aria-label="Close sidebar">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </CloseButton>
              </div>
            </>
          )}
        </HeaderContainer>
        {loading ? (
          <LoaderContainer>
            <Loader>
              <div className="dots-container">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </Loader>
            <div className="loading-text">AI analyzing visualization</div>
          </LoaderContainer>
        ) : (
          chatContent
        )}
      </SidebarContainer>
    </OuterContainer>
  );
}

AiSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  chartRef: PropTypes.instanceOf(Element),
};
